import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  padding: 24px;
  background: #f5f7fa;
  min-height: 100vh;
`;

const PageHeader = styled.header`
  background: white;
  padding: 24px 32px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  margin-bottom: 24px;
  border-left: 4px solid #667eea;
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 4px;
`;

const PageSubtitle = styled.p`
  color: #718096;
  font-size: 0.875rem;
`;

const AnalyticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
`;

const AnalyticsCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 20px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  
  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1a202c;
    margin: 0;
  }
  
  i {
    color: #667eea;
    font-size: 1rem;
  }
`;

const CardContent = styled.div`
  padding: 24px;
`;

const StatItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
  
  &:last-child {
    border-bottom: none;
  }
`;

const StatLabel = styled.span`
  color: #64748b;
  font-size: 0.875rem;
`;

const StatValue = styled.span`
  color: #1e293b;
  font-weight: 600;
  font-size: 0.875rem;
`;

const ChartPlaceholder = styled.div`
  height: 200px;
  background: #f8fafc;
  border: 2px dashed #cbd5e1;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 0.875rem;
`;

const Analytics = () => {
  const [analytics, setAnalytics] = useState({
    totalViewers: 0,
    peakViewers: 0,
    averageWatchTime: '0:00',
    totalStreams: 0,
    streamDuration: '0:00',
    bandwidth: '0 MB'
  });

  useEffect(() => {
    // Simulate analytics data
    const fetchAnalytics = async () => {
      try {
        const response = await fetch('http://159.198.65.99:3000/api/status', {
          headers: {
            'Authorization': 'Bearer holytv_api_key_2024_secure_streaming_server'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setAnalytics({
            totalViewers: data.totalViewers || 0,
            peakViewers: Math.floor((data.totalViewers || 0) * 1.5),
            averageWatchTime: '12:34',
            totalStreams: data.activeStreams || 0,
            streamDuration: '2:15:30',
            bandwidth: '1.2 GB'
          });
        }
      } catch (error) {
        console.error('Error fetching analytics:', error);
      }
    };

    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Analytics</PageTitle>
        <PageSubtitle>Monitor your streaming performance and viewer statistics</PageSubtitle>
      </PageHeader>

      <AnalyticsGrid>
        <AnalyticsCard>
          <CardHeader>
            <i className="fas fa-users"></i>
            <h3>Viewer Statistics</h3>
          </CardHeader>
          <CardContent>
            <StatItem>
              <StatLabel>Total Viewers</StatLabel>
              <StatValue>{analytics.totalViewers}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Peak Viewers</StatLabel>
              <StatValue>{analytics.peakViewers}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Average Watch Time</StatLabel>
              <StatValue>{analytics.averageWatchTime}</StatValue>
            </StatItem>
          </CardContent>
        </AnalyticsCard>

        <AnalyticsCard>
          <CardHeader>
            <i className="fas fa-broadcast-tower"></i>
            <h3>Stream Statistics</h3>
          </CardHeader>
          <CardContent>
            <StatItem>
              <StatLabel>Active Streams</StatLabel>
              <StatValue>{analytics.totalStreams}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Stream Duration</StatLabel>
              <StatValue>{analytics.streamDuration}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Bandwidth Used</StatLabel>
              <StatValue>{analytics.bandwidth}</StatValue>
            </StatItem>
          </CardContent>
        </AnalyticsCard>

        <AnalyticsCard>
          <CardHeader>
            <i className="fas fa-chart-line"></i>
            <h3>Performance Chart</h3>
          </CardHeader>
          <CardContent>
            <ChartPlaceholder>
              <div>
                <i className="fas fa-chart-line" style={{ fontSize: '2rem', marginBottom: '8px' }}></i>
                <div>Viewer Growth Chart</div>
                <div style={{ fontSize: '0.75rem', marginTop: '4px' }}>Coming Soon</div>
              </div>
            </ChartPlaceholder>
          </CardContent>
        </AnalyticsCard>

        <AnalyticsCard>
          <CardHeader>
            <i className="fas fa-globe"></i>
            <h3>Geographic Data</h3>
          </CardHeader>
          <CardContent>
            <ChartPlaceholder>
              <div>
                <i className="fas fa-globe" style={{ fontSize: '2rem', marginBottom: '8px' }}></i>
                <div>Viewer Locations</div>
                <div style={{ fontSize: '0.75rem', marginTop: '4px' }}>Coming Soon</div>
              </div>
            </ChartPlaceholder>
          </CardContent>
        </AnalyticsCard>
      </AnalyticsGrid>
    </PageContainer>
  );
};

export default Analytics;
